import UnknownError from "common/errors/UnknownError";
import PresentableError from "common/errors/PresentableError";
import AuthenticationError from "common/errors/AuthenticationError";
import ModifiedInConcurrentSessionError from "common/errors/ModifiedInConcurrentSessionError";
import RequestError from "common/errors/RequestError";
import "angular-material";
import "common/services/services";

export default function errorPresenterPresenterProvider() {
    //real service
    this.$get = function $get(
        $mdDialog,
        $mdToast,
        $state,
        $log,
        $stateParams,
        $sbAuth,
        $injector,
        $exceptionHandler
    ) {
        "ngInject";
        function ErrorPresenter(config) {
            this.config = config;
            this.catch = this.showWithPreferredPresentationStyle.bind(this);
        }

        ErrorPresenter.prototype.transformToPresentableError = function (
            error
        ) {
            if (error instanceof PresentableError) {
                return error;
            } else {
                return new UnknownError(error);
            }
        };

        ErrorPresenter.prototype.showWithPreferredPresentationStyle = function (
            error,
            presentationStyle,
            redirectOptions = {
                name: $state.current.name,
                params: $state.params,
            }
        ) {
            var presentableError = this.transformToPresentableError(error);

            // On authentication errors we sign out the user and redirect to the login page.
            // We continue to also present why the user has been signed out by showing the error message as a toast.
            //
            if (presentableError instanceof AuthenticationError) {
                $sbAuth.signOut(redirectOptions);
            }

            // A concurrent session error means another user has unpublished
            // changes on the same deliverable - expected, recoverable
            // business state, not a bug. Handling it here means every
            // endpoint that can return it is covered automatically, without
            // each call site needing to detect and wire it up itself.
            //
            // $sbDialog and $sbTracking are resolved lazily via $injector,
            // rather than as regular constructor dependencies: this service
            // is injected by ~50 other services/controllers, many of which
            // are unit-tested with a narrow set of mocked providers. Making
            // this a hard dependency would force every one of those tests to
            // also satisfy $sbDialog's full provider chain, even though most
            // never hit this branch.
            //
            if (presentableError instanceof ModifiedInConcurrentSessionError) {
                $injector
                    .get("$sbTracking")
                    .leanBoard.session.concurrentSession(
                        "Tried to change a deliverable that has been changed in another session"
                    );
                return $injector
                    .get("$sbDialog")
                    .openModifiedInConcurrentSessionDialog(presentableError);
            }

            if (
                error instanceof Error &&
                !(presentableError instanceof AuthenticationError)
            ) {
                $exceptionHandler(error);
            }

            var preferredPresentationStyle =
                presentationStyle ||
                presentableError.preferredPresentationStyle();

            switch (preferredPresentationStyle) {
                case PresentableError.presentationStyle.TOAST:
                    return this.showToast(error);
                case PresentableError.presentationStyle.NONE:
                    return;
                case PresentableError.presentationStyle.DIALOG:
                default:
                    return this.showDialog(error);
            }
        };

        ErrorPresenter.prototype.showToast = function (error) {
            var presentableError = this.transformToPresentableError(error);
            return $mdToast.show(
                $mdToast
                    .error()
                    .content(presentableError.message)
                    .actions(presentableError.getActions())
                    .autoClose(!!presentableError.shouldAutoClose())
                    .hideDelay(
                        presentableError.shouldAutoClose() ? 3000 : false
                    )
                    .icon(presentableError.getIcon())
            );
        };

        ErrorPresenter.prototype.showDialog = function (error, targetEvent) {
            var presentableError = this.transformToPresentableError(error);
            return $mdDialog.show(
                $mdDialog
                    .error()
                    .title(presentableError.title)
                    .ariaLabel(presentableError.title)
                    .content(presentableError.message)
                    .contentValues(presentableError.context)
                    .actions(presentableError.getActions())
                    .clickOutsideToClose(!!presentableError.shouldAutoClose())
                    .escapeToClose(!!presentableError.shouldAutoClose())
                    .autoClose(!!presentableError.shouldAutoClose())
                    .icon(presentableError.getIcon())
                    .targetEvent(targetEvent)
            );
        };

        ErrorPresenter.prototype.isAuthError = function (error) {
            return error instanceof AuthenticationError;
        };

        ErrorPresenter.prototype.isUnknownError = function (error) {
            return (
                error instanceof UnknownError || error instanceof RequestError
            );
        };

        return new ErrorPresenter({});
    };
}
